import { IFormDefinition, ILookupsContext, isFunction, isPromiseLike, LookupCollectionResponse, ResponseStatus } from '@ngt/opms';
import { useMemo } from 'react';
import { KeyedMutator, MutatorCallback } from 'swr';
import { GetConsentEventDataResponse } from '../../api/dtos';
import useConsentEventData from '../data/useConsentEventData';



const useGenerateEConsentLookupsContext = (formDefinition: IFormDefinition | undefined, consentEventContext: ReturnType<typeof useConsentEventData>) => {
    
    const lookupsContext: ILookupsContext = useMemo(() => {
        const lookups = formDefinition?.id ? consentEventContext.data?.lookups[formDefinition.id] ?? [] : []
        const error = consentEventContext.error;

        const lookupsMutate: KeyedMutator<LookupCollectionResponse> = async (data, opts) => {
            if (!formDefinition?.id) {
                return {
                    lookups: [],
                    responseStatus: error as ResponseStatus
                };
            }

            let newData: GetConsentEventDataResponse = { ...(consentEventContext.data as any), lookups: { ...consentEventContext.data?.lookups }, responseStatus: error as any };
            let newSingleData: LookupCollectionResponse | undefined = undefined;
            
            if (!data) {
                newSingleData = undefined;
            }
            else if (isFunction(data)) {
                const existingSingleData = { lookups, responseStatus: error } as LookupCollectionResponse;

                const callback = (data as MutatorCallback<LookupCollectionResponse>)(existingSingleData);
                
                if (isPromiseLike(callback)) {
                    newSingleData = await (callback as Promise<LookupCollectionResponse>);
                }
                else {
                    newSingleData = callback;
                }
            }
            else if (isPromiseLike(data)) {
                newSingleData = await (data as Promise<LookupCollectionResponse>);
            }

            if (!newSingleData) {
                delete newData.lookups[formDefinition.id];
            }
            else {
                if (newSingleData.responseStatus) {
                    newData.responseStatus = newSingleData.responseStatus;
                }

                if (newData.lookups) {
                    if (newSingleData.lookups) {
                        newData.lookups[formDefinition.id] = newSingleData.lookups
                    }
                    else {
                        delete newData.lookups[formDefinition.id];
                    }
                }
                else {
                    if (newSingleData.lookups) {
                        newData.lookups = { [formDefinition.id]: newSingleData.lookups};
                    }
                }
            }

            const updatedData = await consentEventContext.mutate(newData, opts as any);

            if (updatedData) {
                return {
                    lookups: updatedData.lookups[formDefinition.id],
                    responseStatus: updatedData.responseStatus
                } as LookupCollectionResponse
            }

            return undefined;
        }

        const lookupsRefresh = async () => {
            const newData = await consentEventContext.refresh();

            return formDefinition?.id ? newData?.lookups[formDefinition.id] : [];
        }

        return {
            data: lookups,
            error: consentEventContext.error,
            loading: consentEventContext.loading,
            validating: consentEventContext.validating,
            mutate: lookupsMutate,
            refresh: lookupsRefresh
        }
    }, [consentEventContext, formDefinition])

    return lookupsContext
}

export default useGenerateEConsentLookupsContext;